import type { App } from 'vue';

import { CaptureConsole } from '@sentry/integrations';
import * as Sentry from '@sentry/vue';

import legacyLogger from '@evidentid/universal-framework/logger';
import trackOnlineStatusInSentry from '@evidentid/vue-commons/trackOnlineStatusInSentry';

const isSentryBlocked = import.meta.env.DEV;
const dsn = import.meta.env.WEB_PUBLIC_SENTRY_DSN;
const environment = import.meta.env.WEB_PUBLIC_ENVIRONMENT || 'development';

const isSentryAvailable = dsn && !isSentryBlocked;

if (isSentryAvailable) {
    const SINGLE_SESSION_ERRORS_LIMIT = 50;
    let errorsCount = 0;

    // Initialized without an `app` on purpose: this module is imported before
    // createApp() runs, so startup errors are captured from the first moment.
    // Vue-level error hooking is attached later via attachSentryToVueApp() in
    // main.ts's appPlugins (restores what PRODUCT-26110 Task 14 had to drop).
    Sentry.init({
        dsn,
        environment,
        maxBreadcrumbs: 100,
        beforeSend: (event) => {
            errorsCount++;
            return errorsCount >= SINGLE_SESSION_ERRORS_LIMIT ? null : event;
        },
        integrations: [
            new CaptureConsole({
                levels: [ 'error' ],
            }),
        ],
    });
    Sentry.setTag('Application', 'tprm-portal');
    // Track User's connection status
    trackOnlineStatusInSentry(Sentry);
    // Set sentry in legacy logger as well
    legacyLogger.setSentry(Sentry);
} else if (isSentryBlocked) {
    console.warn('Sentry is not initialized - it was blocked for current environment.');
} else {
    console.warn('Sentry is not initialized - DSN was not provided.');
}

export function attachSentryToVueApp(app: App): void {
    if (isSentryAvailable) {
        // Wraps the already-registered app.config.errorHandler and chains it,
        // so the application's own error handling keeps working. VueOptions
        // makes the tracing fields mandatory; these are the SDK defaults and
        // are unused by the error handler itself.
        Sentry.attachErrorHandler(app, {
            attachProps: false,
            logErrors: true,
            trackComponents: false,
            timeout: 2000,
            hooks: [ 'activate', 'mount', 'update' ],
        });
    }
}

export default isSentryAvailable ? Sentry : null;
